<template>
  <div>
    <wc-data-box
      :title='$store.state.icons.dataBox.mechanical.title'
      :titleIcon='$store.state.icons.dataBox.mechanical.icon'
      :titleIconColor='$store.state.icons.dataBox.mechanical.iconColor'
      :showEdit='!project.archived'
      @edit='editProject'
    >
      <v-simple-table dense>
        <tbody>
          <tr>
            <td :class='label'>Controller</td>
            <td v-if='project.project_detail.is_manual_system' :class='dataValue'>
              None - Manual System
            </td>
            <td v-else :class='dataValue'>
              {{ project.project_detail.controller ? project.project_detail.controller_obj.description : "(No controller selected)" }}
            </td>
          </tr>
           <tr>
            <td colspan='2' style='padding-left: 14px; padding-right: 6px;'>
              <table width='100%'>
                <tr>
                  <td :class='label' colspan='2'>
                    The controller is located...<span style='font-size: 10px' class='ml-3'></span>
                  </td>
                </tr>
                <tr>
                  <td class='text-right' colspan='2'>
                    <span class='text--right'>{{ project.project_detail.is_manual_system ? "(n/a)" : project.project_detail.controller_location }}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td colspan='2' style='padding-left: 14px; padding-right: 6px;'>
              <table width='100%'>
                <tr>
                  <td :class='label' colspan='2'>
                    The shutoff is located... <span style='font-size: 10px' class='ml-3'></span>
                  </td>
                </tr>
                <tr>
                  <td class='text-right' colspan='2'>
                    <span class='text--right'>{{ project.project_detail.shutoff_location }}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <tr>
            <td :class='label'>Flow Test <br/> <span style='font-size: 10px'>Seconds to fill 5 gallon bucket</span></td>
            <td :class='dataValue'>{{ project.project_detail.flow_rate }}</td>
          </tr>
          <tr>
            <td :class='label'>GPM <br/> <span style='font-size: 10px'>90% Flow Rate at 35PSI</span></td>
            <td :class='dataValue'>
              <v-chip small class='error mr-3' v-if='flowRate < 5 && flowRate !== 0'>
                <v-icon small dark left>mdi-alert</v-icon>
                LOW FLOW
              </v-chip>
              {{ flowRate }}
            </td>
          </tr>
          <tr>
            <td colspan='2' style='padding-left: 14px; padding-right: 6px;'>
              <table width='100%'>
                <tr>
                  <td :class='label' colspan='2'>
                    Low flow notes <span style='font-size: 10px' class='ml-3'>(Will show on phones)</span>
                  </td>
                </tr>
                <tr>
                  <td class='text-right' colspan='2'>
                    <span v-if='project.project_detail.low_flow_notes' :class='dataValue'>{{ project.project_detail.low_flow_notes }}</span>
                    <span v-else class='grey--text text--lighten-1'>none</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </wc-data-box>

    <wc-dialog
      :showDialog='showDialog'
      :disableButtons='!isFormValid'
      :loadingButtons='isLoading'
      title='Edit Mechanical Details'
      @cancel='showDialog = false'
      @save='saveProject'
      width='400'
    >
      <v-form v-model='isFormValid' ref='form'>
        <v-row>
          <v-col cols='12'>
            <v-checkbox
              v-model='projectCopy.project_detail.is_manual_system'
              label='This is a manual system (no controller)'
              hint='saving this selection will delete the controller & location text'
              :persistent-hint='projectCopy.project_detail.is_manual_system && !project.project_detail.is_manual_system'
            ></v-checkbox>
            <v-autocomplete
              :items='controllers'
              item-value='id'
              item-text='description'
              v-model='projectCopy.project_detail.controller'
              label='Controller'
              :loading='loadingControllers'
              :disabled='projectCopy.project_detail.is_manual_system'
              :hint='projectCopy.project_detail.is_manual_system ? "(manual system selected)" : ""'
              :persistent-hint='projectCopy.project_detail.is_manual_system'
            ></v-autocomplete>
          </v-col>
          <v-col cols='12'>
            <v-textarea
              v-model='projectCopy.project_detail.controller_location'
              :rules='[sentenceRules.noPeriod, sentenceRules.startLowercase]'
              label='The controller is located...'
              :hint='projectCopy.project_detail.is_manual_system ? "(manual system selected)" : "must complete the sentence above (or be empty)"'
              persistent-hint
              rows='1'
              outlined
              auto-grow
              :disabled='projectCopy.project_detail.is_manual_system'
            ></v-textarea>
          </v-col>
          <v-col cols='12'>
            <v-textarea
              v-model='projectCopy.project_detail.shutoff_location'
              :rules='[sentenceRules.noPeriod, sentenceRules.startLowercase]'
              label='The shutoff is located...'
              :hint='shutoffHint'
              persistent-hint
              rows='1'
              outlined
              auto-grow
            ></v-textarea>
          </v-col>
          <v-col cols='12'>
            <v-text-field
              label='Flow Test'
              v-model='projectCopy.project_detail.flow_rate'
              :rules='[rules.int]'
              hint='how many seconds to fill a 5 gallon pail at 35psi'
            ></v-text-field>
          </v-col>
          <v-col cols='12'>
            <v-textarea
              label='Low Flow Notes'
              v-model='projectCopy.project_detail.low_flow_notes'
              rows='1'
              outlined
              auto-grow
              placeholder='none'
            ></v-textarea>
          </v-col>
        </v-row>
      </v-form>
    </wc-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import irrigationAPI from '@/modules/irrigation/api/irrigations'
import formRules from '@/mixins/formRules'

export default {
  name: 'Mechanical',
  mixins: [formRules],
  props: {
    project: {
      required: true
    }
  },
  data: () => ({
    isLoading: false,
    isFormValid: false,
    showDialog: false,
    loadingControllers: false,
    controllers: [],
    label: 'grey--text text-left',
    projectCopy: { project_detail: {} },
    contacts: [],
    dataValue: 'text-right pr-2'
  }),
  computed: {
    flowRate () {
      let gpm = 0
      if (this.project.project_detail.flow_rate > 0 && this.project.project_detail.flow_rate !== null && this.project.project_detail.flow_rate !== '') {
        gpm = 60 / parseFloat(this.project.project_detail.flow_rate)
        gpm = parseFloat(gpm * 5 * .9)
      } else {
        return 0
      }
      return gpm.toFixed(1)
    },
    shutoffHint () {
      if (this.project.project_detail.is_hose_bib)  {
        return "Please note - Hose bib supply selected"
      } 
      return "must complete the sentence above (or be empty)"
    },
    sentenceRules() {
      return {
        noPeriod: (value) => {
          const pattern = /^[^.]+$|null|^\s*$/
          return pattern.test(value) || 'cannot contain or end with a period'
        },
        startLowercase: (value) => {
          // expression contains negative lookahead so that empty is valid.
          const pattern = /^([a-z])|^(?![\s\S])/
          return pattern.test(value) || 'must start with lowercase letter'
        },
      }
    }
  },
  methods: {
    async editProject () {
      this.showDialog = true
      this.loadingControllers = true
      this.projectCopy = _.cloneDeep(this.project)
      this.controllers = await irrigationAPI.getControllers()
      this.$refs.form.validate()
      this.loadingControllers = false
    },
    saveProject () {
      this.isLoading = true
      if (this.projectCopy.project_detail.is_manual_system) {
        this.projectCopy.project_detail.controller_location = ""
        this.projectCopy.project_detail.controller = null
      }
      this.$emit('update', this.projectCopy)
      this.showDialog = false
      this.isLoading = false
    }
  }
}
</script>
